import { LoadingSpinner, Button, toastPop } from "@talladega/components";
import LoginFlowWrapper from "@common/components/LoginFlowWrapper";
import CoreLayout from "@layouts/core/components/CoreLayout";
import PublicLayout from "@layouts/public/components/PublicLayout";
import useUser from "@modules/user/hooks/useUser";
import { loginMode } from "@modules/user/types/LoginModalMode";
import NotFound from "@pages/404";
import classNames from "classnames";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactNode, useCallback, useEffect, useState } from "react";
import getSourceDetails from "@modules/appraisal/helpers/sourceLookup";
import { AppraisalMethodEnum } from "@modules/appraisal/types/AppraisalSource";
import {
  getPreviousStep,
  icoPersonalDetailsStep,
  PurchaseStep,
} from "@modules/purchase/types/PurchaseStep";
import { ArrowLeftIcon, ArrowUpIcon, ChevronDownIcon } from "@talladega/icons";
import { useTranslation } from "next-i18next";
import useGetPurchasesByDealIds from "@modules/purchase/hooks/useGetPurchasesByDealIds";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import { Disclosure, Transition } from "@headlessui/react";
import getUserDealIdsByType from "@modules/purchase/helpers/getUserDealIdsByType";
import useLocalStorage from "@common/hooks/useLocalStorage";
import ThrottleTransitionModal from "@modules/purchase/components/offer/ThrottleTransitionModal";
import NavBar from "@layouts/public/components/NavBar";
import useHappyFox from "@modules/happyfox/useHappyFox";
import TradeInSourceName from "@modules/trade/enums/TradeInSourceName";
import useTradeInFromToken from "@modules/trade/hooks/useTradeInFromToken";
import useClaimIcoForAuthenticatedUserMutation from "@modules/purchase/hooks/useClaimIcoForAuthenticatedUserMutation";
import InstantCashOfferSummary, {
  InstantCashOfferSummaryHeader,
} from "./InstantCashOfferSummary";

type Props = {
  children: ReactNode;
  removePadding?: boolean;
  showSummary?: boolean;
  onLoginSuccess?: () => void;
  onLoginClose?: () => void;
  headerContainer?: ReactNode;
  currentStep?: PurchaseStep;
  showBackButton?: boolean;
  showPurchaseInfo?: boolean;
};

const InstantCashOfferLayout = ({
  removePadding,
  showSummary,
  onLoginSuccess,
  onLoginClose,
  children,
  headerContainer,
  currentStep,
  showBackButton,
  showPurchaseInfo = true,
}: Props) => {
  const [isNotInIframe, setIsInIframe] = useState<boolean>(true);

  useEffect(() => {
    setIsInIframe(window === window.top);
  }, []);

  useHappyFox({ isEnabled: isNotInIframe });

  const router = useRouter();
  const { t } = useTranslation(["common", "purchase"]);
  const {
    source,
    dealId,
    transitionToThrottle,
    tradeIn: tradeInToken,
  } = router.query;
  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [disableClosingLoginModal, setDisableClosingLoginModal] =
    useState<boolean>(false);
  const [showThrottleTransitionModal, setShowThrottleTransitionModal] =
    useState<boolean>(false);
  const [throttleTransitionModalSeen, setThrottleTransitionModalSeen] =
    useLocalStorage<boolean>("throttleTransitionModalSeen");
  const {
    user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
    refetch: refetchUser,
  } = useUser();
  const validBuyInDealId = getUserDealIdsByType(user, DealTypeEnum.BuyIn).find(
    (x) => x === dealId
  );

  const { activePurchases, isLoading: isPurchaseLoading } =
    useGetPurchasesByDealIds(validBuyInDealId ? [validBuyInDealId] : []);

  const { tradeIn: tradeInFromToken } = useTradeInFromToken({
    token: tradeInToken as string | null,
    source: source as string | null,
  });
  const claimIcoForAuthenticatedUserMutation =
    useClaimIcoForAuthenticatedUserMutation();

  const handleOnLoginSuccess = useCallback(async () => {
    // must have trade-in token and check if user is finished logging in
    if (tradeInToken) {
      try {
        await refetchUser();
        await claimIcoForAuthenticatedUserMutation.mutateAsync({
          source: source as string,
          tradeInToken: tradeInToken as string,
        });
      } catch (ex) {
        // TODO: we have a ticket to have a better error handling
        // for now if they fail to claim the ico, kick them back to home page
        toastPop(
          "warning",
          t("common:error"),
          t("purchase:email_doesnt_match")
        );
        router.push({ pathname: "/" });
      }
    }

    onLoginSuccess?.();
  }, [
    claimIcoForAuthenticatedUserMutation,
    onLoginSuccess,
    refetchUser,
    router,
    source,
    t,
    tradeInToken,
  ]);

  useEffect(() => {
    if (
      !tradeInToken ||
      !tradeInFromToken ||
      !tradeInFromToken.isDealAlreadyCreated ||
      isUserFetching
    )
      return;

    if (
      tradeInFromToken.isAssociatedWithAuthenticatedUser &&
      !tradeInFromToken.isAssociatedUser
    ) {
      if (user?.isLoggedIn) {
        // if they signed-in and are not the associated user, kick them back to home page
        router.push({ pathname: "/" });
      } else {
        // otherwise, force auth
        setShowLoginModal(true);
        setDisableClosingLoginModal(true);
      }
    }
  }, [
    isUserFetching,
    router,
    tradeInFromToken,
    tradeInToken,
    user?.isLoggedIn,
  ]);

  const purchase = activePurchases[0];

  const tradeIn =
    purchase?.tradeIns && purchase.tradeIns.length > 0
      ? purchase.tradeIns[0]
      : null;

  const ymm = [tradeIn?.year, tradeIn?.make, tradeIn?.model]
    .filter(Boolean)
    .join(" ");
  const trimMileage = [
    tradeIn?.trim,
    `${tradeIn?.mileage?.toLocaleString()} ${t("common:kilometers")}`,
  ]
    .filter(Boolean)
    .join(" | ");

  useEffect(() => {
    if (
      router.isReady &&
      transitionToThrottle &&
      !throttleTransitionModalSeen
    ) {
      setShowThrottleTransitionModal(true);
      setThrottleTransitionModalSeen(true);
    }
  }, [
    transitionToThrottle,
    router.isReady,
    setThrottleTransitionModalSeen,
    throttleTransitionModalSeen,
    dealId,
    router,
  ]);

  const previousStep =
    showBackButton && currentStep
      ? getPreviousStep("ico", currentStep)
      : undefined;

  const onLoginModalClose = () => {
    if (onLoginClose) {
      onLoginClose();
    }
    setShowLoginModal(false);
  };

  if (
    source &&
    !getSourceDetails(
      (source as string).toLocaleLowerCase() as TradeInSourceName
    ).allowedMethods.find((m) => m === AppraisalMethodEnum.InstantCashOffer)
  ) {
    return router.isReady ? (
      <PublicLayout>
        <NotFound />
      </PublicLayout>
    ) : (
      <div />
    );
  }

  return (
    <CoreLayout>
      <LoginFlowWrapper
        isLoginModalOpen={showLoginModal}
        onLoginModalClose={onLoginModalClose}
        onLogin={() => handleOnLoginSuccess()}
        loginModalMode={loginMode}
        disableClosingModal={disableClosingLoginModal}
        onEmailConfirmed={() => handleOnLoginSuccess()}
      >
        <div
          className={classNames("bg-white", {
            "lg:h-screen": false,
          })}
        >
          <NavBar user={user} setShowLoginModal={setShowLoginModal} />
          {showPurchaseInfo && (
            <div className="bg-gray-25 p-4 sticky top-0 z-50 lg:hidden">
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="w-full">
                      <div className="flex items-center justify-between lg:hidden bg-gray-25">
                        {ymm && trimMileage && (
                          <div className="flex flex-col gap-1 items-start px-4 flex-1">
                            <p className="font-semibold">{ymm}</p>
                            <span className="text-gray-500">{trimMileage}</span>
                          </div>
                        )}
                        <ChevronDownIcon
                          className={open ? "rotate-180 transform" : ""}
                        />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel>
                      <Transition
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                      >
                        {tradeIn && (
                          <div className="flex flex-col justify-center px-4 pt-4">
                            <InstantCashOfferSummaryHeader tradeIn={tradeIn} />
                            <Disclosure.Button>
                              <Button
                                fill="link"
                                leftIcon={<ArrowUpIcon className="h-4" />}
                              >
                                {t("common:show_less")}
                              </Button>
                            </Disclosure.Button>
                          </div>
                        )}
                      </Transition>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          )}

          <div
            id="ico-children-container"
            className="flex lg:min-h-[calc(100vh-148px)] xl:min-h-[calc(100vh-92px)]"
          >
            <div
              className={classNames("flex-auto", {
                "px-4 py-8 lg:px-12 xl:px-28 xl:py-12 w-4/6": !removePadding,
              })}
            >
              {headerContainer && headerContainer}
              {previousStep && purchase?.dealSource && (
                <div className="hidden lg:flex mx-6 mb-6 ml-2 items-center">
                  <Link
                    className="flex gap-x-2 text-md font-semibold text-primary-bold"
                    href={{
                      pathname: previousStep.href,
                      query:
                        // if the back button take user to ico landing page, only keep the source
                        previousStep.id !== icoPersonalDetailsStep.id
                          ? router.query
                          : {
                              source: purchase.dealSource.toLowerCase(),
                            },
                    }}
                  >
                    <ArrowLeftIcon className="w-5 h-5" />
                    <span>
                      {t("common:back")}{" "}
                      {previousStep
                        ? `${t("common:to")} ${t(previousStep.nameTranslation)}`
                        : ""}
                    </span>
                  </Link>
                </div>
              )}
              {children}
            </div>
            {showSummary && (
              <div className="bg-gray-25 w-1/3 hidden lg:block">
                <div className="sticky top-12">
                  {tradeIn && <InstantCashOfferSummary tradeIn={tradeIn} />}
                </div>
              </div>
            )}
          </div>
        </div>
      </LoginFlowWrapper>
      <LoadingSpinner
        isLoading={isUserLoading || isPurchaseLoading}
        isOverlay
      />
      <ThrottleTransitionModal
        isOpen={showThrottleTransitionModal}
        onClose={() => setShowThrottleTransitionModal(false)}
      />
    </CoreLayout>
  );
};

export default InstantCashOfferLayout;
